import { FormField } from '@/components/ui/form-field'
import ConfigurationIcons from '@/icons/ConfigurationIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import PricingIcons from '@/icons/PricingIcons'
import { PRICING_MODELS, PRODUCT_UNITS } from '@/modules/Contract/constants'
import {
    Contract,
    ServiceConfigOption,
    SingleProductConfigOptionProps,
} from '@/modules/Contract/types'
import { useEffect } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    Checkbox,
    Chip,
    GridHookForm,
    IconButton,
    Typogaphy,
} from 'vnetwork-ui'
import PricingSection from '../shared/PricingSection'

interface ConfigProps extends SingleProductConfigOptionProps {
    isPOC?: boolean
    servicePriceModel?: string
}

const Config: React.FC<ConfigProps> = ({
    index,
    name,
    isPOC = false,
    servicePriceModel = 'package',
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()

    const resizeable = useWatch({
        control: formControl.control,
        name: `${name}.[${index}].resizable` as Path<Contract>,
    })

    const statementable = useWatch({
        control: formControl.control,
        name: `${name}.[${index}].statementable` as Path<Contract>,
    })

    const commitmentValue =
        useWatch({
            control: formControl.control,
            name: `${name}.[${index}].commitment_value` as Path<Contract>,
        }) || 0

    const commitmentPricePerUnit =
        useWatch({
            control: formControl.control,
            name: `${name}.[${index}].commitment_price_per_unit` as Path<Contract>,
        }) || 0

    useEffect(() => {
        if (servicePriceModel !== PRICING_MODELS.Bundle) return

        formControl.setValue(
            `${name}.[${index}].commitment_price_total` as Path<Contract>,
            Number(commitmentValue || 0) * Number(commitmentPricePerUnit || 0),
            { shouldDirty: true }
        )
    }, [
        commitmentValue,
        commitmentPricePerUnit,
        formControl,
        index,
        name,
        servicePriceModel,
    ])

    // Handle statementable change
    const handleStatementableChange = (checked: boolean) => {
        formControl.setValue(
            `${name}.[${index}].statementable` as Path<Contract>,
            checked
        )

        if (checked) {
            // Initialize with single pricing when enabled
            formControl.setValue(
                `${name}.[${index}].prices` as Path<Contract>,
                [
                    {
                        price_start: 0,
                        price_end: 999999999,
                        price_per_unit: 0,
                    },
                ]
            )
        } else {
            formControl.setValue(
                `${name}.[${index}].prices` as Path<Contract>,
                []
            )
        }
    }

    const handleRemoveConfigOption = () => {
        const configoptions = formControl.getValues(
            `${name}` as Path<Contract>
        ) as ServiceConfigOption[]
        const newConfigoptions = configoptions.filter(
            (_: any, i: number) => i !== index
        )
        formControl.setValue(`${name}` as Path<Contract>, newConfigoptions)
    }

    return (
        <div className="bg-white rounded border border-slate-200 hover:border-slate-300 transition-colors duration-200 mb-3">
            {/* Header */}
            <div className="px-3 py-2.5 bg-slate-50/50 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <ConfigurationIcons className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <Typogaphy className="font-medium text-slate-800 text-sm truncate uppercase">
                                {t(
                                    formControl.getValues(
                                        `${name}.[${index}].name` as Path<Contract>
                                    )
                                )}
                            </Typogaphy>
                        </div>
                        {resizeable && (
                            <Chip
                                label={t('configurable')}
                                size="small"
                                color="info"
                                variant="contained"
                                className="text-xs"
                            />
                        )}
                    </div>
                    {resizeable && (
                        <IconButton
                            color="error"
                            size="small"
                            onClick={handleRemoveConfigOption}
                            className="hover:bg-red-50 ml-2"
                        >
                            <DeleteOutlineIcons className="w-4 h-4" />
                        </IconButton>
                    )}
                </div>
            </div>

            {/* Configuration Fields */}
            <div className="px-3 pt-2.5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <GridHookForm
                        subForm
                        formControl={formControl}
                        className="grid-cols-4"
                        data={[
                            {
                                renderEditComponent: () => (
                                    <FormField
                                        control={formControl.control}
                                        name={
                                            `${name}.[${index}].commitment_value` as Path<Contract>
                                        }
                                        type="number"
                                        label={t('package-parameters')}
                                        placeholder={t('package-parameters')}
                                    />
                                ),
                                className:
                                    servicePriceModel !== PRICING_MODELS.Bundle
                                        ? 'col-span-2'
                                        : 'col-span-1',
                            },
                            {
                                formItemProps: {
                                    name: `${name}.[${index}].unit` as Path<Contract>,
                                    type: 'select',
                                    label: t('unit of calculation'),
                                    options: PRODUCT_UNITS(t),
                                    placeholder: t('unit of calculation'),
                                    disabled: true,
                                },
                            },
                            {
                                renderEditComponent: () => (
                                    <FormField
                                        control={formControl.control}
                                        name={
                                            `${name}.[${index}].commitment_price_per_unit` as Path<Contract>
                                        }
                                        type="number"
                                        label={t('commitment_price_per_unit')}
                                        placeholder={t(
                                            'commitment_price_per_unit'
                                        )}
                                    />
                                ),
                                className:
                                    servicePriceModel !== PRICING_MODELS.Bundle
                                        ? 'hidden'
                                        : '',
                            },
                            {
                                renderEditComponent: () => (
                                    <div className="bg-slate-50/50 px-3 rounded border border-slate-200/60">
                                        <div className="flex items-center gap-2 justify-between">
                                            <Checkbox
                                                label={t(
                                                    'contract.monthly_statement'
                                                )}
                                                checked={statementable || false}
                                                onChange={(event) =>
                                                    handleStatementableChange(
                                                        event?.target?.checked
                                                    )
                                                }
                                                className="text-sm"
                                            />
                                            {statementable && (
                                                <Chip
                                                    label={t('enabled')}
                                                    size="small"
                                                    color="success"
                                                    variant="contained"
                                                    className="text-xs"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ),
                                className: isPOC ? 'hidden' : '',
                            },
                        ]}
                        actions={[]}
                    />
                </div>
            </div>
            {statementable && !isPOC && (
                <div className="border-t border-slate-200/60">
                    <div className="px-3 py-2.5 bg-indigo-50/50 border-b border-indigo-200/60">
                        <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 bg-indigo-100 rounded flex items-center justify-center flex-shrink-0">
                                <PricingIcons className="w-3.5 h-3.5 text-indigo-600" />
                            </div>
                            <Typogaphy className="font-medium text-indigo-800 text-sm">
                                {t('contract.pricing_configuration')}
                            </Typogaphy>
                        </div>
                    </div>
                    <div className="px-3 py-2.5">
                        <PricingSection
                            name={name}
                            index={index}
                            formControl={formControl}
                            commitmentValue={commitmentValue}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default Config
